import * as React from 'react';
import { PageSection, Title, Card, CardBody, CardTitle, CardFooter, Button, Modal, ModalVariant, Wizard, WizardStep, WizardHeader, List, ListItem } from '@patternfly/react-core';
import ExternalLinkSquareAltIcon from '@patternfly/react-icons/dist/esm/icons/external-link-square-alt-icon';
import { useNavigate, useLocation } from 'react-router-dom';
import { AUTH_CONFIG, PARTNER_ID, PRODUCT_NAME } from '../../config/partner';

const entraUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?prompt=consent&client_id=${AUTH_CONFIG.ENTRA_CLIENT_ID}&response_mode=form_post&response_type=code%20id_token&nonce=KEtXs2KwH6&sso_reload=true&scope=openid%20profile%20https%3A%2F%2Fgraph.microsoft.com%2F.default&state=removeGraph-${PARTNER_ID}`;
const logoutUrl = "https://login.microsoftonline.com/common/oauth2/v2.0/logout";

const EntraOffboarding: React.FunctionComponent = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [step, setStep] = React.useState(1);
  const [hasLoggedOut, setHasLoggedOut] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Handle URL changes for wizard
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('step');
    if (stepParam) {
      const stepNumber = parseInt(stepParam, 10);
      if (!isNaN(stepNumber) && stepNumber >= 1 && stepNumber <= 5) {
        setStep(stepNumber);
        setIsModalOpen(true);
      }
    }
  }, [location]);

  const handleStepChange = (event: any, currentStep: any) => {
    const newStep = Number(currentStep.id);
    setStep(newStep);
    navigate(`${location.pathname}?step=${newStep}`, { replace: true });
  };

  return (
    <PageSection>
      <Title headingLevel="h1" size="xl">{PRODUCT_NAME} Entra Offboarding</Title>
      <br />
      <Card>
        <CardTitle>Revoke access for 'Combined {PRODUCT_NAME} - Microsoft Entra and MS365'</CardTitle>
        <CardBody>
          <p>This will remove the {PRODUCT_NAME} application's access to your Entra ID tenant.</p>
          <p>After removal, {PRODUCT_NAME} will no longer be able to access your Microsoft Entra and MS365 data.</p>
        </CardBody>
        <CardFooter>
          <Button variant="danger" onClick={() => {
            setStep(1);
            navigate(`${location.pathname}?step=1`, { replace: true });
            setIsModalOpen(true);
          }}>
            Revoke Access
          </Button>
        </CardFooter>
      </Card>

      <Modal
        isOpen={isModalOpen}
        aria-label={`${PRODUCT_NAME} Entra Offboarding Wizard Modal`}
        onEscapePress={() => {
          setIsModalOpen(false);
          navigate(location.pathname, { replace: true });
        }}
        variant={ModalVariant.medium}
      >
        <Wizard
          height={500}
          onClose={() => {
            setIsModalOpen(false);
            navigate(location.pathname, { replace: true });
          }}
          title="Entra wizard"
          startIndex={step}
          onStepChange={handleStepChange}
          isVisitRequired
          header={
            <WizardHeader
              onClose={() => {
                setIsModalOpen(false);
                navigate(location.pathname, { replace: true });
              }}
              title="Revoke Entra and MS365 Access"
              description={`Follow these steps to remove ${PRODUCT_NAME}'s access to your environment.`}
            />
          }
        >
          <WizardStep name="Introduction" id="1">
            <List>
              <ListItem>This wizard will guide you through the process of revoking {PRODUCT_NAME}'s access to your Entra ID tenant.</ListItem>
              <ListItem>After completion, {PRODUCT_NAME} will no longer be able to access your Microsoft Entra and MS365 data.</ListItem>
              <ListItem>You can always re-enable access by running the {PRODUCT_NAME} process again.</ListItem>
            </List>
          </WizardStep>
          <WizardStep name="Accept permissions" id="2" footer={{ isNextDisabled: true }}>
            <List>
              <ListItem>Follow the link below to be redirected to Microsoft.</ListItem>
              <ListItem>You will be prompted again accept the permissions used by {PRODUCT_NAME}.</ListItem>
              <ListItem>'Read and write all applications' is requested to allow {PRODUCT_NAME} to delete the Service Principal associated with {PRODUCT_NAME}.</ListItem>
            </List>
            <br />
            <a href={entraUrl} rel="noopener noreferrer">
              <Button variant="danger" icon={<ExternalLinkSquareAltIcon />} iconPosition="end">Remove Application</Button>
            </a>
          </WizardStep>
          <WizardStep name="Application removed" id="3">
            <p>The {PRODUCT_NAME} application has been successfully removed from your environment.</p>
            <p>You can now proceed to the next step.</p>
          </WizardStep>
          <WizardStep name="Logout" id="4" footer={{ isNextDisabled: !hasLoggedOut }}>
            <p>To revoke session cookies used offboarding, please logout from Microsoft before proceeding:</p>
            <br/>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Button 
                variant="primary" 
                icon={<ExternalLinkSquareAltIcon />} 
                iconPosition="end"
                onClick={() => {
                  window.open(logoutUrl, 'Microsoft Logout', 'width=500,height=600,left=100,top=100');
                  setHasLoggedOut(true);
                }}
              >
                Logout from Microsoft
              </Button>
              <p>(Opens in a new window)</p>
            </div>
          </WizardStep>
          <WizardStep
            name="Access Revoked"
            id="5"
            footer={{ 
              nextButtonText: 'Finish', 
              onNext: () => {
                setIsModalOpen(false);
                navigate(location.pathname, { replace: true });
              }
            }}
          >
            <b>Success!</b>
            <br/>
            <br/>
            {PRODUCT_NAME}'s access to your Entra ID tenant has been revoked.
            <br/>
            <br/>
            You can always re-enable access by running the {PRODUCT_NAME} process again.
          </WizardStep>
        </Wizard>
      </Modal>
    </PageSection>
  );
};

export { EntraOffboarding }; 